import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import moment from 'moment';
import { ENVIRONMENT_CONFIG, EnvironmentConfig } from '@ipreo/northstar';
import { MuniEnvironmentParams } from '../types/muni-environment';
import {
  USTreasuryResponse,
  USTreasuryResponseData,
  USTreasury,
} from '../types/treasuries.types';
import { PlatformType } from '../types/muni-environment';

@Injectable()
export class TreasuriesService {
  private apiHost: string;
  private platform: PlatformType;

  private readonly platforms: { [key: string]: any } = {
    [PlatformType.polaris]: {
      dateFormat: 'MM-DD-YYYY',
      apiRoute: '/api/v1/us-treasuries',
    },
    [PlatformType.buyside]: {
      dateFormat: 'YYYY-MM-DD',
      apiRoute: '/v1/us-treasuries',
    },
  };

  constructor(
    private readonly http: HttpClient,
    @Inject(ENVIRONMENT_CONFIG) config: EnvironmentConfig
  ) {
    this.apiHost = (config.params as MuniEnvironmentParams).apiHost;
    this.platform = config.params.platform as PlatformType;
  }

  public fetchData(date: string): Observable<USTreasury[]> {
    const requestedDate: string = moment(date, 'YYYY-MM-DD').format(
      this.platforms[this.platform].dateFormat
    );

    return this.http
      .get(
        `${this.apiHost}${
          this.platforms[this.platform].apiRoute
        }?date=${requestedDate}`,
        {
          observe: 'response',
        }
      )
      .pipe(
        mergeMap((response: HttpResponse<USTreasuryResponse[]>) => {
          const data = response.status === 200 && response.body;

          return data
            ? of(this.mapResponseToTreasuries(data))
            : throwError(Error('Cannot load US Treasuries'));
        })
      );
  }

  private mapResponseToTreasuries(
    resTreasuries: USTreasuryResponse[]
  ): USTreasury[] {
    return resTreasuries.map((treasury) => {
      return {
        securityId: treasury.securityId,
        current: this.toTreasuryData(treasury.current),
        previous: this.toTreasuryData(treasury.previous),
      };
    });
  }

  private toTreasuryData(data: USTreasuryResponseData) {
    return {
      ...data,
      date: moment(data.date),
      maturity: moment(data.maturity),
    };
  }
}
